import { useEffect, useRef, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import { DEG2RAD } from "three/src/math/MathUtils";
import { OrbitControls } from "@react-three/drei";
import { DeviceOrientationControlsCustom } from "./DeviceOrientationControlsCustom";
import { Locations } from "./Location";
import { lerp } from "../Util";
import { DEFAULT_FADE_DURATION_MS } from "./VideoConfig.jsx"; 

// 1. Extract default array outside the component to prevent infinite re-renders
const DEFAULT_CAM_POS = [0, 0, 0];

export function CustomControls({
	gyroSettings, 
	needsReset, setNeedsReset,
	camHeightOffset = 1.7, 
    camPosition = DEFAULT_CAM_POS, // <-- Use the stable constant reference
    heading = 0 
}) {
	const camera = useThree(state => state.camera);
    const controlsRef = useRef(); 
	const maxPolarAngle = (90.0 - 1.0) * DEG2RAD; 

    const target = useMemo(() => [
        camPosition[0], 
        camPosition[1] + camHeightOffset, 
        camPosition[2] - 0.1
    ], [camPosition[0], camPosition[1], camPosition[2], camHeightOffset]);

    // This will now strictly only run when the camera position ACTUALLY changes, 
    // completely preventing the premature immediate snap.
	useEffect(() => {
		camera.position.set(camPosition[0], camPosition[1] + camHeightOffset, camPosition[2]);
	}, [camHeightOffset, camPosition, camera]);

    useEffect(() => {
        if (!gyroSettings.useGyro) {
            const timer = setTimeout(() => {
                if (controlsRef.current) {
                    // 2. Bypass damping to allow the instant snap
                    const prevDamping = controlsRef.current.enableDamping;
                    controlsRef.current.enableDamping = false;

                    // Set the angle and force the update
                    controlsRef.current.setAzimuthalAngle(heading);
                    controlsRef.current.update();

                    // Restore the damping settings for the user
                    controlsRef.current.enableDamping = prevDamping;
                }
            }, DEFAULT_FADE_DURATION_MS);
            
            return () => clearTimeout(timer); 
        }
    }, [heading, gyroSettings.useGyro]);

	if (gyroSettings.useGyro) {
		// Mobile controls
		return <DeviceOrientationControlsCustom
			camera={camera} 
			needsReset={needsReset} setNeedsReset={setNeedsReset}
            heading={heading} 
		/>;
	} else {
		// Browser controls
		return <OrbitControls
            ref={controlsRef} 
			makeDefault
			camera={camera}
			target={target}
			enableZoom={false}
			enablePan={false}
			enableDamping={true}
			dampingFactor={0.05}
			minDistance={0.1}
			maxDistance={0.1}
			minPolarAngle={Math.PI / 2} 
			maxPolarAngle={Math.PI / 2}
			rotateSpeed={-0.3} 
		/>;
	}
}